import { useEffect } from "react";
import BaseLayout from "layouts/sections/components/BaseLayout";
import MKBox from "components/MKBox";
import DefaultFooter from "examples/Footers/DefaultFooter";
import footerRoutes from "footer.routes";

import bgImage from "assets/images/fondo-serviciosimp.jpeg";
import imagenOutsourcing from "assets/images/Carrusel 1.png";
import imagenSoporte from "assets/images/Carrusel 2.png";
import imagenControl from "assets/images/Carrusel 3.png";
import imagenSeguridad from "assets/images/Carrusel 4.png";
import imagenSuministros from "assets/images/Carrusel 5.png";

import {
  TrendingDownIcon,
  EyeOffIcon,
  NetworkIcon,
  BoxesIcon,
  AlarmClockIcon,
  ChartColumnIcon,
  ChartLineIcon,
  CircleCheckIcon,
} from "components/KinitechLanding/icons";
import QuoteSection from "components/KinitechLanding/QuoteSection";
import FaqSection from "components/KinitechLanding/FaqSection";
import HeroCarousel from "components/KinitechLanding/HeroCarousel";
import useScrollReveal from "components/KinitechLanding/useScrollReveal";
import { StickyCta } from "components/KinitechLanding/CtaBand";
import { scrollToContacto } from "components/KinitechLanding/scroll";
import "components/KinitechLanding/styles.css";
import { initTracking } from "tracking";

const description = "Outsourcing y alquiler de impresión en Cali";

const heroSlides = [
  {
    eyebrow: "Outsourcing e impresión en Cali",
    headline: "OUTSOURCING Y ALQUILER DE IMPRESORAS HP EN CALI",
    paragraphs: [
      "Optimiza la gestión de tus equipos de impresión en Cali con outsourcing y alquiler de impresoras HP para empresas.",
      "Alquiler de impresoras HP, suministros originales, soporte técnico y gestión de impresión en un solo servicio.",
      "También conocido como Managed Print Services (MPS), nuestro servicio te ayuda a modernizar tu parque de impresión y mantener tu operación bajo control.",
    ],
    closing: "Conoce nuestra solución de impresión empresarial en Cali.",
    primary: { label: "Solicitar diagnóstico", target: "contacto" },
    secondary: { label: "Conocer la solución", target: "problemas-impresion" },
    image: imagenOutsourcing,
    imageAlt: "Impresoras y multifuncionales HP en una oficina",
    caption: "Outsourcing de impresión",
    captionText:
      "Impresoras monofuncionales, multifuncionales y escáneres para optimizar tu operación.",
  },
  {
    eyebrow: "Soporte técnico",
    headline: "SOPORTE TÉCNICO ESPECIALIZADO",
    subtitle: "Tus impresoras no pueden detener la operación de tu empresa.",
    paragraphs: [
      "Contamos con servicio técnico de impresoras, mantenimiento preventivo y correctivo para equipos de impresión empresarial.",
      "Nuestro objetivo es mantener tus equipos disponibles, detectar fallas oportunamente y prolongar la vida útil de tus impresoras.",
      "Soporte de impresoras para empresas con atención especializada en Cali.",
    ],
    closing: "Mantén tu operación siempre en marcha.",
    primary: { label: "Solicitar soporte", target: "contacto" },
    secondary: { label: "Ver cómo funciona", target: "como-funciona" },
    image: imagenSoporte,
    imageAlt: "Técnico de KINITECH configurando una multifuncional HP",
    caption: "Soporte técnico especializado",
    captionText:
      "Mantenimientos preventivos y correctivos para mantener tus equipos siempre disponibles.",
  },
  {
    eyebrow: "Control y auditoría",
    headline: "CONTROLA TUS COSTOS DE IMPRESIÓN",
    subtitle: "¿Sabes cuánto cuesta realmente imprimir en tu empresa?",
    paragraphs: [
      "Con un software de control de impresión puedes realizar el monitoreo de impresiones, conocer el volumen por usuario y equipo e identificar oportunidades para reducir costos.",
      "Obtén información para calcular tu costo por página y tomar decisiones.",
    ],
    closing: "Convierte tus impresiones en información para ahorrar.",
    primary: { label: "Quiero reducir costos", target: "contacto" },
    secondary: { label: "Hablar con un asesor", target: "contacto" },
    image: imagenControl,
    imageAlt: "Mujer revisando un panel de control de impresiones",
    caption: "Controla tus impresiones. Reduce tus costos.",
    captionText:
      "Monitorea usuarios, equipos, volúmenes y consumos desde un solo lugar.",
  },
  {
    eyebrow: "Seguridad",
    headline: "IMPRIME DE FORMA SEGURA",
    subtitle: "Tus documentos también necesitan protección.",
    paragraphs: [
      "Con soluciones de impresión segura y Pull Print, los documentos permanecen protegidos hasta que el usuario se identifica directamente en el equipo.",
      "Una solución que contribuye al control de impresión empresarial, reduce documentos olvidados en las bandejas y ayuda a proteger información sensible.",
    ],
    closing: "Protege la información que imprime tu empresa.",
    primary: { label: "Proteger mis documentos", target: "contacto" },
    secondary: { label: "Hablar con un asesor", target: "contacto" },
    image: imagenSeguridad,
    imageAlt: "Mujer junto a una multifuncional HP con un documento protegido",
    caption: "Imprime con seguridad",
    captionText:
      "Protege la información que pasa por tus equipos de impresión y controla el acceso a tus documentos.",
  },
  {
    eyebrow: "Suministros HP originales",
    headline: "SUMINISTROS HP ORIGINALES PARA IMPRESIÓN EN CALI",
    subtitle: "Evita que la falta de tóner detenga tu operación.",
    paragraphs: [
      "Gestionamos el suministro de tóner HP original y consumibles de impresión para empresas en Cali.",
      "Con una adecuada gestión de consumibles y control de tóner, puedes optimizar el suministro y evitar compras de emergencia.",
      "Suministros HP originales para impresoras empresariales, con abastecimiento continuo y calidad confiable.",
    ],
    closing: "Optimiza la gestión de tus consumibles HP originales.",
    primary: { label: "Optimizar mis consumibles", target: "contacto" },
    secondary: { label: "Ver suministros HP", target: "/impresion/consumibles" },
    image: imagenSuministros,
    imageAlt: "Tóner HP original junto a una impresora",
    caption: "Tóner HP original, rendimiento confiable",
    captionText:
      "Suministro de consumibles originales para garantizar calidad, rendimiento y confiabilidad. Suministro continuo.",
  },
];

const problems = [
  {
    icon: TrendingDownIcon,
    title: "Costos elevados de impresión",
    text: "El gasto de impresión crece sin que puedas identificar por qué. Consumos dispersos y equipos de diferentes tecnologías dificultan el control y la optimización de los costos de impresión.",
  },
  {
    icon: EyeOffIcon,
    title: "Falta de control",
    text: "¿Sabes quién imprime, cuánto imprime y dónde? Sin monitoreo de impresiones y control de impresión empresarial, es difícil identificar consumos innecesarios y oportunidades de ahorro.",
  },
  {
    icon: NetworkIcon,
    title: "Gestión de múltiples equipos",
    text: "Administrar un parque de impresión heterogéneo consume tiempo de TI. Diferentes equipos, proveedores, tecnologías y necesidades de mantenimiento dificultan una adecuada gestión de impresión empresarial.",
  },
  {
    icon: BoxesIcon,
    title: "Control de consumibles",
    text: "¿Tienes tóner de sobra o siempre te enteras cuando se acaba? Las compras reactivas, el sobrestock y el desabastecimiento dificultan la gestión de consumibles y el suministro oportuno de tóner para empresas.",
  },
  {
    icon: AlarmClockIcon,
    title: "Interrupciones operativas",
    text: "Una impresora detenida puede detener procesos importantes. Las fallas, mantenimientos no programados y falta de consumibles afectan la continuidad de la operación y aumentan la necesidad de mantenimiento de impresoras empresariales y soporte técnico de impresoras.",
  },
  {
    icon: ChartColumnIcon,
    title: "Falta de información para decidir",
    text: "Sin datos, es difícil saber cuándo renovar, cuánto invertir o dónde ahorrar. La falta de información sobre equipos, volúmenes y costos dificulta la planificación del presupuesto.",
  },
];

const mpsServices = [
  "Outsourcing de impresión",
  "Soporte técnico especializado",
  "Gestión de tóner y consumibles",
  "Monitoreo de impresiones",
  "Control y optimización de costos",
  "Renovación y modernización del parque de impresión",
];

const steps = [
  {
    title: "Diagnóstico",
    text: "Evaluamos su parque de impresión, equipos, sedes, referencias de tóner HP y volúmenes de impresión para conocer sus necesidades reales de suministro.",
  },
  {
    title: "Análisis",
    text: "Analizamos el consumo de tóner y suministros de impresión, costos, frecuencia de compra y posibles puntos de ineficiencia.",
  },
  {
    title: "Diseño de la solución",
    text: "Definimos el esquema de abastecimiento de tóner HP original, cobertura, referencias, consumibles y niveles de servicio según la operación de su empresa.",
  },
  {
    title: "Implementación",
    text: "Coordinamos el suministro e instalación de consumibles HP, configuración y puesta en marcha del modelo de abastecimiento.",
  },
  {
    title: "Seguimiento",
    text: "Realizamos seguimiento al consumo de tóner y suministros HP, con reportes, ajustes y acompañamiento para garantizar la continuidad de su operación de impresión.",
  },
];

const faqItems = [
  {
    question: "¿Qué incluye el outsourcing de impresión?",
    answer: [
      "Equipos, consumibles, mantenimiento, soporte y reportes de consumo bajo un mismo modelo de servicio.",
    ],
  },
  {
    question: "¿Debemos cambiar todos nuestros equipos?",
    answer: [
      "No necesariamente. El diagnóstico define qué equipos se conservan y cuáles conviene reemplazar.",
    ],
  },
  {
    question: "¿Cómo se controla el costo?",
    answer: [
      "Se trabaja sobre un esquema medible por volumen, lo que permite presupuestar el gasto de impresión.",
    ],
  },
  {
    question: "¿Funciona para empresas con varias sedes?",
    answer: [
      "Sí. El modelo se diseña por sede y se consolida la gestión y los reportes de manera centralizada.",
    ],
  },
  {
    question: "¿Cuánto tarda la implementación?",
    answer: [
      "Depende del tamaño del parque; el cronograma se define al cierre del diseño de la solución.",
    ],
  },
  {
    question: "¿Cómo inicio el proceso?",
    answer: [
      "Solicite el diagnóstico desde el formulario y coordinamos el levantamiento de información.",
    ],
  },
];

const necesidadOptions = [
  "Diagnóstico de impresión",
  "Outsourcing completo",
  "Control de costos",
  "Gestión de consumibles",
  "Soporte y mantenimiento",
];

function OutsourcingLandingBody() {
  const revealRef = useScrollReveal();

  useEffect(() => {
    initTracking("outsourcing");
  }, []);

  return (
    <div className="kn-landing" ref={revealRef}>
      <HeroCarousel slides={heroSlides} />

      <section className="kn-section" id="problemas-impresion">
        <div className="kn-container">
          <div data-reveal="up">
            <span className="kn-eyebrow">Impresión empresarial en Cali</span>
            <h2 className="kn-section-title">
              ¿Tu empresa en Cali tiene problemas de impresión u outsourcing?
            </h2>
            <p className="kn-section-lead">
              El alquiler de impresoras HP y la gestión de impresión pueden
              convertirse en un costo invisible cuando no existe un outsourcing
              adecuado.
            </p>
          </div>
          <div className="kn-problems-grid" data-reveal-group>
            {problems.map(({ icon: Icon, title, text }) => (
              <article
                key={title}
                className="kn-card kn-card--surface"
                data-reveal="flip"
              >
                <span className="kn-icon-chip">
                  <Icon />
                </span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>

          <div className="kn-mps-box" data-reveal="zoom">
            <p className="kn-eyebrow">Managed Print Services (MPS)</p>
            <h3>
              Transforma estos problemas en una gestión de impresión más
              eficiente.
            </h3>
            <p>
              Con Kinitech, tu empresa puede centralizar la gestión de sus
              equipos de impresión, soporte técnico, consumibles, monitoreo y
              control de costos mediante una solución integral de Outsourcing de
              Impresión.
            </p>
            <ul className="kn-mps-list" data-reveal-group>
              {mpsServices.map((service) => (
                <li key={service} data-reveal="up">
                  <CircleCheckIcon />
                  {service}
                </li>
              ))}
            </ul>
            <div className="kn-hero-actions">
              <button
                type="button"
                className="kn-btn-primary"
                onClick={scrollToContacto}
              >
                Quiero optimizar mi impresión
              </button>
              <button
                type="button"
                className="kn-btn-secondary"
                onClick={scrollToContacto}
              >
                Hablar con un asesor
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="kn-section kn-section--surface" id="como-funciona">
        <div className="kn-container">
          <div className="kn-steps-heading" data-reveal="up">
            <span className="kn-eyebrow">Cómo funciona</span>
            <h2 className="kn-section-title">Un modelo en cinco etapas</h2>
          </div>
          <ol className="kn-steps" data-reveal="line" data-reveal-group>
            {steps.map(({ title, text }, index) => (
              <li key={title} className="kn-step" data-reveal="right">
                <span className="kn-step-number">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <QuoteSection
        eyebrow="Diagnóstico"
        title="Solicite su diagnóstico de impresión"
        lead="Un asesor empresarial coordinará el levantamiento de información de su parque de impresión."
        options={necesidadOptions}
        submitLabel="Solicitar diagnóstico"
        subjectPrefix="Diagnóstico outsourcing de impresión"
        trackingKey="outsourcing"
      />

      <FaqSection items={faqItems} />

      <section className="kn-section kn-section--flush-top">
        <div className="kn-container">
          <div className="kn-cta-card" data-reveal="zoom">
            <div>
              <span className="kn-eyebrow">
                <ChartLineIcon /> Gestión empresarial
              </span>
              <h2>¿Necesita una solución para la impresión de su empresa?</h2>
            </div>
            <button
              type="button"
              className="kn-btn-primary kn-btn-primary--block kn-btn-primary--mobile-full"
              onClick={scrollToContacto}
            >
              Solicitar cotización
            </button>
          </div>
        </div>
      </section>

      <StickyCta />
    </div>
  );
}

const EmptySection = () => null;

function Navbars() {
  return (
    <>
      <MKBox bgColor="white">
        <BaseLayout
          bgImage={bgImage}
          logoSolution=""
          desc={description}
          cardsComponent={EmptySection}
          soloHeader
          parallax
          whitewash
          flushBottom
          bgPosition="center bottom"
        />
        <OutsourcingLandingBody />
      </MKBox>
      <MKBox mt="auto" pt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default Navbars;
